import { colorString, type PDFContext, PDFDocument, type PDFRef, setFillingColor, setStrokingColor } from '@pdfme/pdf-lib';
import type { AppearanceStreamParams, PDFAnnotationInput, PDFVertex } from './pdf-annotations.types';

export const getAnnotationBox = (boundingBox: PDFVertex[], pageSize: { width: number; height: number }) => {
    if (boundingBox.length < 2) {
        return;
    }

    const bounds = boundingBox.reduce(
        (acc, { x, y }) => ({
            minX: Math.min(acc.minX, x),
            maxX: Math.max(acc.maxX, x),
            minY: Math.min(acc.minY, y),
            maxY: Math.max(acc.maxY, y),
        }),
        {
            minX: Infinity,
            maxX: -Infinity,
            minY: Infinity,
            maxY: -Infinity,
        },
    );

    const left = bounds.minX * pageSize.width;
    const right = bounds.maxX * pageSize.width;
    const top = pageSize.height - bounds.minY * pageSize.height;
    const bottom = pageSize.height - bounds.maxY * pageSize.height;
    const width = right - left;
    const height = top - bottom;

    if (width <= 0 || height <= 0) {
        return;
    }

    return {
        left,
        right,
        top,
        bottom,
        width,
        height,
    };
};

export const generateAppearanceStream = ({
    box,
    fill,
    stroke,
    strokeWidth,
    opacity,
}: AppearanceStreamParams): Parameters<PDFContext['stream']> => {
    // inset the rectangle to account for the stroke width so that the stroke is drawn inside the bounding box
    const inset = strokeWidth / 2;

    // the contents and dictionary context.stream() expects to build how the annotation is drawn
    return [
        [
            // apply the GS graphics state defined below (opacity + blend)
            '/GS gs',
            // fill colour
            setFillingColor(fill),
            // border colour
            setStrokingColor(stroke),
            // rectangle, inset by half the border width so the border isn't clipped
            `${inset} ${inset} ${box.width - strokeWidth} ${box.height - strokeWidth} re`,
            // paint: fill and stroke the rectangle — without it the path is built but never drawn
            'B',
        ].join('\n'),
        {
            // a reusable drawing (form XObject); required by the spec
            Subtype: 'Form',
            // drawing area; the viewer scales it onto the annotation's Rect
            BBox: [0, 0, box.width, box.height],
            // named graphics states, used by '/GS gs'
            Resources: {
                ExtGState: {
                    GS: {
                        // fill opacity
                        ca: opacity,
                        // blend mode; darkens instead of covering so the text stays readable
                        BM: 'Multiply',
                    },
                },
            },
        },
    ];
};

const createAnnotation = (pdfDocument: PDFDocument, input: PDFAnnotationInput): PDFRef | undefined => {
    if (input.page < 1 || input.page > pdfDocument.getPageCount()) {
        return;
    }

    const pageSize = pdfDocument.getPage(input.page - 1).getSize(); // resolves the dimensions of the annotation page
    const box = getAnnotationBox(input.boundingBox, pageSize);

    if (!box) return;

    const { left, right, top, bottom } = box;

    // colors follow a numeric rgb format with each channel ranging from 0 to 1 (e.g. [0, 0, 0] is black and [1, 1, 1] is white)
    const fill = colorString(input.fill).rgb;
    const stroke = colorString(input.stroke).rgb;

    const appearance = pdfDocument.context.stream(
        ...generateAppearanceStream({
            box,
            fill,
            stroke,
            strokeWidth: input.strokeWidth,
            opacity: input.opacity,
        }),
    );

    const annotation = pdfDocument.context.obj({
        // marks this dictionary as an annotation
        Type: 'Annot',
        // the annotation kind: a rectangle
        Subtype: 'Square',
        // position on the page in points: left, bottom, right, top (origin bottom-left)
        Rect: [left, bottom, right, top],
        // appearance from drawing built above
        AP: { N: pdfDocument.context.register(appearance) },
    });

    return pdfDocument.context.register(annotation);
};

/** annotates a raw pdf blob */
export const annotatePDF = async (blob: Blob, annotationInputs: PDFAnnotationInput[]): Promise<Blob> => {
    const pdfDocument = await PDFDocument.load(await blob.arrayBuffer());

    annotationInputs.forEach(annotationInput => {
        const annotation = createAnnotation(pdfDocument, annotationInput);

        if (annotation) {
            pdfDocument.getPage(annotationInput.page - 1).node.addAnnot(annotation);
        }
    });

    const bytes = await pdfDocument.save();

    return new Blob([bytes], { type: 'application/pdf' });
};
